import {
  DEFAULT_APPEARANCE,
  DEFAULT_FOLDER_TAG_COLOR,
  DEFAULT_SAVE_MODE,
  ROOT_FOLDER_ID,
  SCRATCHPAD_CONTENT,
  SCRATCHPAD_EDITOR_STATE,
  STYLE_DEMO_CONTENT,
  STYLE_DEMO_EDITOR_STATE,
  WELCOME_CONTENT,
  WELCOME_EDITOR_STATE,
} from './constants';

export function createId(prefix) {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function nowISO() {
  return new Date().toISOString();
}

export function normalizeName(value) {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}

export function cleanFolderName(value) {
  return value.trim().replace(/[\\/]/g, '-').replace(/\s+/g, ' ');
}

export function cleanFileName(value) {
  return value
    .trim()
    .replace(/\.txt$/i, '')
    .replace(/[\\/]/g, '-')
    .replace(/\s+/g, ' ');
}

export function getFileDisplayName(file) {
  return `${file.name}${file.extension || '.txt'}`;
}

export function appearanceMatches(baseAppearance, candidateAppearance) {
  const base = { ...DEFAULT_APPEARANCE, ...(baseAppearance || {}) };
  const candidate = { ...DEFAULT_APPEARANCE, ...(candidateAppearance || {}) };

  return (
    base.backgroundColor === candidate.backgroundColor &&
    base.fontSize === candidate.fontSize &&
    base.fontFamily === candidate.fontFamily &&
    base.lineHeight === candidate.lineHeight &&
    base.textColor === candidate.textColor
  );
}

export function createDefaultWorkspace() {
  const createdAt = nowISO();
  const projectsId = createId('folder');
  const notesId = createId('folder');

  return {
    version: 1,
    folders: [
      {
        id: ROOT_FOLDER_ID,
        name: 'TextLab',
        parentId: null,
        tagColor: null,
        usesDefaultTag: false,
        isFavorite: false,
        createdAt,
        updatedAt: createdAt,
      },
      {
        id: projectsId,
        name: 'Projects',
        parentId: ROOT_FOLDER_ID,
        tagColor: '#007aff',
        usesDefaultTag: false,
        isFavorite: true,
        createdAt,
        updatedAt: createdAt,
      },
      {
        id: notesId,
        name: 'Notes',
        parentId: ROOT_FOLDER_ID,
        tagColor: DEFAULT_FOLDER_TAG_COLOR,
        usesDefaultTag: true,
        isFavorite: false,
        createdAt,
        updatedAt: createdAt,
      },
    ],
    files: [
      {
        id: createId('file'),
        name: 'Welcome',
        extension: '.txt',
        content: WELCOME_CONTENT,
        editorState: WELCOME_EDITOR_STATE,
        parentFolderId: ROOT_FOLDER_ID,
        appearance: { ...DEFAULT_APPEARANCE },
        usesDefaultAppearance: true,
        isFavorite: true,
        createdAt,
        updatedAt: createdAt,
      },
      {
        id: createId('file'),
        name: 'Style Demo',
        extension: '.txt',
        content: STYLE_DEMO_CONTENT,
        editorState: STYLE_DEMO_EDITOR_STATE,
        parentFolderId: ROOT_FOLDER_ID,
        appearance: { ...DEFAULT_APPEARANCE, backgroundColor: '#fff7ed' },
        usesDefaultAppearance: false,
        isFavorite: false,
        createdAt,
        updatedAt: createdAt,
      },
      {
        id: createId('file'),
        name: 'Scratchpad',
        extension: '.txt',
        content: SCRATCHPAD_CONTENT,
        editorState: SCRATCHPAD_EDITOR_STATE,
        parentFolderId: notesId,
        appearance: { ...DEFAULT_APPEARANCE },
        usesDefaultAppearance: true,
        isFavorite: false,
        createdAt,
        updatedAt: createdAt,
      },
    ],
    settings: {
      theme: 'light',
      viewMode: 'grid',
      gridSize: 'small',
      explorerFilter: 'all',
      explorerSort: 'nameAsc',
      favoritesOpen: false,
      saveMode: DEFAULT_SAVE_MODE,
      defaultFolderTagColor: DEFAULT_FOLDER_TAG_COLOR,
      defaultFileAppearance: { ...DEFAULT_APPEARANCE },
      lastOpenedFolderId: ROOT_FOLDER_ID,
    },
    updatedAt: createdAt,
  };
}

export function getFolderById(folders, folderId) {
  return folders.find((folder) => folder.id === folderId);
}

export function getFileById(files, fileId) {
  return files.find((file) => file.id === fileId);
}

export function getDescendantFolderIds(folders, folderId) {
  const descendants = [];
  const stack = [folderId];

  while (stack.length) {
    const currentId = stack.pop();
    const children = folders.filter((folder) => folder.parentId === currentId);
    children.forEach((folder) => {
      descendants.push(folder.id);
      stack.push(folder.id);
    });
  }

  return descendants;
}

export function isFolderMoveInvalid(folders, folderId, destinationId) {
  if (folderId === ROOT_FOLDER_ID) {
    return true;
  }

  if (folderId === destinationId) {
    return true;
  }

  return getDescendantFolderIds(folders, folderId).includes(destinationId);
}

export function buildBreadcrumbs(folders, folderId) {
  const folderMap = new Map(folders.map((folder) => [folder.id, folder]));
  const crumbs = [];
  let cursor = folderMap.get(folderId) || folderMap.get(ROOT_FOLDER_ID);

  while (cursor) {
    crumbs.unshift(cursor);
    cursor = cursor.parentId ? folderMap.get(cursor.parentId) : null;
  }

  return crumbs;
}

export function buildFolderPath(folders, folderId) {
  return buildBreadcrumbs(folders, folderId)
    .map((folder) => folder.name)
    .join(' / ');
}

export function buildSearchIndex(folders, files) {
  const folderItems = folders
    .filter((folder) => folder.id !== ROOT_FOLDER_ID)
    .map((folder) => ({
      id: folder.id,
      type: 'folder',
      label: folder.name,
      parentId: folder.parentId,
      path: buildFolderPath(folders, folder.parentId || ROOT_FOLDER_ID),
    }));

  const fileItems = files.map((file) => ({
    id: file.id,
    type: 'file',
    label: getFileDisplayName(file),
    parentId: file.parentFolderId,
    path: buildFolderPath(folders, file.parentFolderId),
  }));

  return [...folderItems, ...fileItems].sort((a, b) => a.label.localeCompare(b.label));
}

export function hasDuplicateName(workspace, { type, name, parentId, excludeId }) {
  const candidate =
    type === 'file'
      ? normalizeName(`${cleanFileName(name)}.txt`)
      : normalizeName(cleanFolderName(name));

  const folderConflict = workspace.folders.some((folder) => {
    if (folder.id === excludeId || folder.parentId !== parentId) {
      return false;
    }

    return normalizeName(folder.name) === candidate;
  });

  if (folderConflict) {
    return true;
  }

  return workspace.files.some((file) => {
    if (file.id === excludeId || file.parentFolderId !== parentId) {
      return false;
    }

    return normalizeName(getFileDisplayName(file)) === candidate;
  });
}

export function validateItemName(workspace, { type, name, parentId, excludeId }) {
  const cleaned = type === 'file' ? cleanFileName(name) : cleanFolderName(name);

  if (!cleaned) {
    return 'Name is required.';
  }

  if (hasDuplicateName(workspace, { type, name: cleaned, parentId, excludeId })) {
    return 'An item with that name already exists here.';
  }

  return '';
}
